/**
 * Notification Edit Modal
 * 
 * Quick edit modal for notification preferences
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Checkbox,
  IconButton,
  Typography,
  Box,
  Stack,
  Divider,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

interface NotificationPreferences {
  email_enabled: boolean;
  sms_enabled: boolean;
  whatsapp_enabled: boolean;
  notification_types: {
    order_updates: boolean;
    payment_reminders: boolean;
    promotional: boolean;
  };
}

interface NotificationEditModalProps {
  open: boolean;
  onClose: () => void;
  partyName: string;
  email?: string;
  mobile?: string;
  preferences?: NotificationPreferences;
  onSave: (preferences: NotificationPreferences) => Promise<void>;
  onSendTest?: (type: 'email' | 'sms' | 'whatsapp') => Promise<void>;
}

const NotificationEditModal: React.FC<NotificationEditModalProps> = ({
  open,
  onClose,
  partyName,
  email,
  mobile,
  preferences,
  onSave,
  onSendTest,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<NotificationPreferences>({
    email_enabled: false,
    sms_enabled: false,
    whatsapp_enabled: false,
    notification_types: {
      order_updates: true,
      payment_reminders: true,
      promotional: false,
    },
  });

  useEffect(() => {
    if (preferences) {
      setFormData(preferences);
    } else {
      setFormData({
        email_enabled: false,
        sms_enabled: false,
        whatsapp_enabled: false,
        notification_types: {
          order_updates: true,
          payment_reminders: true,
          promotional: false,
        },
      });
    }
  }, [preferences, open]);

  const handleToggle = (field: 'email_enabled' | 'sms_enabled' | 'whatsapp_enabled') => {
    setFormData((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleNotificationTypeToggle = (type: 'order_updates' | 'payment_reminders' | 'promotional') => {
    setFormData((prev) => ({
      ...prev,
      notification_types: {
        ...prev.notification_types,
        [type]: !prev.notification_types[type],
      },
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(formData);
      enqueueSnackbar('Notification preferences saved successfully', { variant: 'success' });
      onClose();
    } catch (error: any) {
      console.error('Error saving preferences:', error);
      enqueueSnackbar(error.message || 'Failed to save preferences', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSendTest = async (type: 'email' | 'sms' | 'whatsapp') => {
    if (!onSendTest) return;

    setSendingTest(type);
    try {
      await onSendTest(type);
      enqueueSnackbar(`Test ${type} sent successfully`, { variant: 'success' });
    } catch (error: any) {
      console.error(`Error sending test ${type}:`, error);
      enqueueSnackbar(error.message || `Failed to send test ${type}`, { variant: 'error' });
    } finally {
      setSendingTest(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(90deg, #2196f3 0%, #0d47a1 100%)',
          color: '#ffffff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notification Preferences
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {partyName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'inherit' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Email Notifications */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ color: formData.email_enabled ? 'success.main' : 'text.disabled' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Email Notifications
                </Typography>
              </Box>
              <Switch
                checked={formData.email_enabled}
                onChange={() => handleToggle('email_enabled')}
                color="success"
              />
            </Box>
            {email ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {email}
                </Typography>
                {onSendTest && formData.email_enabled && (
                  <Button
                    size="small"
                    startIcon={<SendIcon />}
                    onClick={() => handleSendTest('email')}
                    disabled={sendingTest === 'email'}
                    sx={{ textTransform: 'none' }}
                  >
                    {sendingTest === 'email' ? 'Sending...' : 'Test'}
                  </Button>
                )}
              </Box>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                No email address configured
              </Alert>
            )}
          </Box>

          <Divider />

          {/* SMS Notifications */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon sx={{ color: formData.sms_enabled ? 'success.main' : 'text.disabled' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  SMS Notifications
                </Typography>
              </Box>
              <Switch
                checked={formData.sms_enabled}
                onChange={() => handleToggle('sms_enabled')}
                color="success"
              />
            </Box>
            {mobile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {mobile}
                </Typography>
                {onSendTest && formData.sms_enabled && (
                  <Button
                    size="small"
                    startIcon={<SendIcon />}
                    onClick={() => handleSendTest('sms')}
                    disabled={sendingTest === 'sms'}
                    sx={{ textTransform: 'none' }}
                  >
                    {sendingTest === 'sms' ? 'Sending...' : 'Test'}
                  </Button>
                )}
              </Box>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                No mobile number configured
              </Alert>
            )}
          </Box>

          <Divider />

          {/* WhatsApp Notifications */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WhatsAppIcon sx={{ color: formData.whatsapp_enabled ? 'success.main' : 'text.disabled' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  WhatsApp Notifications
                </Typography>
              </Box>
              <Switch
                checked={formData.whatsapp_enabled}
                onChange={() => handleToggle('whatsapp_enabled')}
                color="success"
              />
            </Box>
            {mobile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {mobile}
                </Typography>
                {onSendTest && formData.whatsapp_enabled && (
                  <Button
                    size="small"
                    startIcon={<SendIcon />}
                    onClick={() => handleSendTest('whatsapp')}
                    disabled={sendingTest === 'whatsapp'}
                    sx={{ textTransform: 'none' }}
                  >
                    {sendingTest === 'whatsapp' ? 'Sending...' : 'Test'}
                  </Button>
                )}
              </Box>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                No WhatsApp number configured
              </Alert>
            )}
          </Box>

          <Divider />

          {/* Notification Types */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Notification Types
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.notification_types.order_updates}
                    onChange={() => handleNotificationTypeToggle('order_updates')}
                  />
                }
                label="Order Updates"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.notification_types.payment_reminders}
                    onChange={() => handleNotificationTypeToggle('payment_reminders')}
                  />
                }
                label="Payment Reminders"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.notification_types.promotional}
                    onChange={() => handleNotificationTypeToggle('promotional')}
                  />
                }
                label="Promotional Messages"
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: 'none', borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="secondary"
          disabled={loading}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          {loading ? 'Saving...' : 'Save Preferences'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationEditModal;
